import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import axios from "axios";
import {
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  FormHelperText,
} from "@mui/material";
import { toast } from "react-toastify";
function ItemsForm({ selectedItem, setItemModalOpen, fetchItems }) {
  const API_BASE_URL = process.env.REACT_APP_BASE_URL;
  const [itemImage, setItemImage] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [categories, setCategories] = useState([]);

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      status: "1", // Default value for status is "Active"
    },
  });

  useEffect(() => {
    if (selectedItem) {
      setValue("name", selectedItem.name);
      setValue("price", selectedItem.price);
      setValue("portion_qty", selectedItem.portion_qty);
      setValue("category", selectedItem.category);
      setValue("status", selectedItem.status || "1"); // Set to "Active" (1) if not specified
    }
    fetchCategories();
  }, [selectedItem, setValue]);

  const fetchCategories = async () => {
    try {
      const categoryResponse = await axios.get(
        `${API_BASE_URL}/Menu_category/all`
      );
      if (categoryResponse.status === 200) {
        setCategories(categoryResponse.data.items || []);
      } else {
        toast("Failed to fetch categories.");
      }
    } catch (err) {
      toast("An error occurred while fetching categories.");
    }
  };

  const imageConvertToBase64 = (file) => {
    if (file.size <= 500 * 1024) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setItemImage(reader.result.toString());
      };
      reader.readAsDataURL(file);
    } else {
      toast("File size must be less than 500 KB.");
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    imageConvertToBase64(file);
  };

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      const url = selectedItem
        ? `${API_BASE_URL}/Menu_category/form_item/${selectedItem.id}` // Update item
        : `${API_BASE_URL}/Menu_category/form_item`; // Add new item

      const requestData = {
        ...data,
        docpath: itemImage || "",
      };

      const response = await axios.post(url, requestData);

      if (response.status === 200) {
        // Show success toast
        toast(
          selectedItem
            ? "Item updated successfully!"
            : "Item added successfully!"
        );
        fetchItems(); // Refresh the items list
        setItemModalOpen(false);
      } else {
        toast("Error saving item data.");
      }
    } catch (err) {
      toast("An error occurred while submitting the form.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} encType="multipart/form-data">
      <TextField
        label="Item Name"
        variant="outlined"
        fullWidth
        size="small"
        margin="dense"
        {...register("name", { required: "This field is required." })}
        error={!!errors.name}
        helperText={errors.name?.message}
      />

      <TextField
        label="Item Price"
        variant="outlined"
        fullWidth
        size="small"
        margin="dense"
        {...register("price", { required: "This field is required." })}
        error={!!errors.price}
        helperText={errors.price?.message}
      />

      <TextField
        label="Portion Qty"
        variant="outlined"
        fullWidth
        size="small"
        margin="dense"
        {...register("portion_qty", { required: "This field is required." })}
        error={!!errors.portion_qty}
        helperText={errors.portion_qty?.message}
      />

      <div>
        <label>Item Image:</label>
        <input type="file" onChange={handleImageChange} />
        {itemImage && (
          <img
            src={itemImage}
            alt="Item Preview"
            style={{ width: "50px", height: "50px", marginTop: "10px" }}
          />
        )}
      </div>

      <FormControl
        fullWidth
        margin="dense"
        size="small"
        error={!!errors.category}
      >
        <InputLabel>Category</InputLabel>
        <Select
          label="Category"
          {...register("category", { required: "This field is required." })}
        >
          <MenuItem value="">Select Category</MenuItem>
          {categories.map((category) => (
            <MenuItem key={category.id} value={category.id}>
              {category.category_name}
            </MenuItem>
          ))}
        </Select>
        {errors.category && (
          <FormHelperText>{errors.category.message}</FormHelperText>
        )}
      </FormControl>

      <FormControl
        fullWidth
        margin="dense"
        size="small"
        error={!!errors.status}
      >
        <InputLabel>Status</InputLabel>
        <Select
          label="Status"
          {...register("status", { required: "This field is required." })}
        >
          <MenuItem value="1">Active</MenuItem>
          <MenuItem value="0">Inactive</MenuItem>
        </Select>
        {errors.status && (
          <FormHelperText>{errors.status.message}</FormHelperText>
        )}
      </FormControl>

      <Button
        type="submit"
        variant="contained"
        color="primary"
        fullWidth
        size="small"
        disabled={isSubmitting}
      >
        {isSubmitting ? <CircularProgress size={24} /> : "Save"}
      </Button>
    </form>
  );
}

export default ItemsForm;
